import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api, { setToken } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { syncPush } from '@/lib/push';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasSession = useRef(false);

  const loadProfile = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      if (res.data?.notify_enabled) syncPush().catch(() => {});
    } catch (e) {
      setUser(null);
      if (e.response?.status === 403 && !sessionStorage.getItem('geo_blocked_shown')) {
        sessionStorage.setItem('geo_blocked_shown', '1');
        toast.error(e.response?.data?.detail || 'CurbAlert is only available in the United States, Canada, and Mexico.', { duration: 8000 });
      }
    }
  }, []);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      if (data?.session) {
        hasSession.current = true;
        await loadProfile();
      }
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        // Avoid redundant profile reloads on silent token refreshes.
        if (event === 'TOKEN_REFRESHED' && hasSession.current) return;
        hasSession.current = true;
        loadProfile();
      } else {
        hasSession.current = false;
        setUser(null);
      }
    });

    return () => { active = false; sub?.subscription?.unsubscribe(); };
  }, [loadProfile]);

  const login = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + '/auth/callback' },
    });
    if (error) {
      console.error('Supabase Google login failed', error);
      toast.error('Sign-in failed. Please try again.');
    }
  };

  const logout = async () => {
    try { await supabase.auth.signOut(); } catch (e) { console.debug('supabase signout failed:', e?.message || e); }
    setToken(null);
    setUser(null);
  };

  const contextValue = useMemo(
    () => ({ user, setUser, loading, login, logout, refresh: loadProfile }),
    [user, loading, loadProfile]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
